/* eslint-disable indent */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, TableCell, TableHead, TableRow, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useGeneralChargesContext } from 'contexts/GeneralChargesContext';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChargeFormEntry } from '../../types';

const useStyles = makeStyles((theme) => ({
  tableHeadRowFirst: {
    position: 'sticky',
    background: theme.palette.background.paper,
    top: 0,
  },
  tableHeadRowSecond: {
    position: 'sticky',
    background: theme.palette.background.paper,
    top: 56,
  },
  tableHeaderPayee: {
    minWidth: '10rem',
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: "0px",
    [theme.breakpoints.down('sm')]: {
      minWidth: '8rem',
      fontSize: theme.typography.caption.fontSize,
    },
  },
  tableHeaderEndorsement: {
    width: '50px',
  },
  tableHeaderDescription: {
    width: "15rem",
    [theme.breakpoints.down('sm')]: {
      width: '10rem',
      fontSize: theme.typography.caption.fontSize,
    },
  },
  tableHeaderAmountOverrideWarning: {
    maxWidth: '100px',
  },
  tableHeaderNumber: {
    lineHeight: '1rem',
    padding: 0,
    paddingRight: '5px',
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.caption.fontSize,
    },
  },
  tableHeaderByOthers: {
    lineHeight: '1rem',
    padding: 0,
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.caption.fontSize,
    },
  },
  tableHeaderAction: {
    width: '50px',
    textAlign: 'center',
  },
  payerIcon: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

interface Props {
  lettersPerMonth: string[];
  hideTax: boolean;
  currentLetter?: string;
  isOrderTypeNotHUD1: boolean;
  onEditSplitPayers: (charge: ChargeFormEntry, kind: "Payee" | "Payer" | "Tax", editAllCharges?: boolean) => void
}

export const ChargesTableHead: FC<Props> = ({
  lettersPerMonth,
  hideTax,
  currentLetter,
  isOrderTypeNotHUD1,
  onEditSplitPayers
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const hairline = `1px solid ${theme.palette.divider}`;

  const { hideEndorsementCode } = useGeneralChargesContext();

  const getColSpan = useCallback(() => {
    if (!hideEndorsementCode) return 7;
    const initSpan = (currentLetter && lettersPerMonth.includes(currentLetter)) ? 8 : 6;
    return hideTax ? initSpan : initSpan + 1;
  }, [hideTax, currentLetter, lettersPerMonth, hideEndorsementCode]);

  return (
    <TableHead>
      {isOrderTypeNotHUD1 && (
        <TableRow className={classes.tableHeadRowFirst}>
          <TableCell align="center" colSpan={getColSpan()} />
          <TableCell
            align="center"
            colSpan={2}
            style={{
              borderLeft: hairline,
              borderRight: hairline,
            }}
          >
            {t('charges:paid-by-borrower')}
          </TableCell>
          <TableCell
            align="center"
            colSpan={2}
            style={{
              borderRight: hairline,
            }}
          >
            {t('charges:paid-by-seller')}
          </TableCell>
          <TableCell />
          <TableCell />
        </TableRow>
      )}
      <TableRow
        className={isOrderTypeNotHUD1 ? classes.tableHeadRowSecond : classes.tableHeadRowFirst}
      >
        <TableCell />
        <TableCell />
        {!hideEndorsementCode && (
          <TableCell className={classes.tableHeaderEndorsement}>{t('charges:endorsement')}</TableCell>
        )}
        <TableCell className={classes.tableHeaderDescription}>{t('charges:description')}</TableCell>
        <TableCell className={classes.tableHeaderPayee}>
          <>{t('charges:payee')}</>
          <FontAwesomeIcon
            icon="users"
            style={{ marginLeft: "10px", cursor: "pointer" }}
            onClick={() => onEditSplitPayers({} as ChargeFormEntry, "Payee", true)}
          />
        </TableCell>
        <TableCell className={classes.tableHeaderAmountOverrideWarning} />
        <TableCell className={classes.tableHeaderNumber}>{t('accounting:amount')}</TableCell>

        {currentLetter && lettersPerMonth.includes(currentLetter) && (
          <>
            <TableCell className={classes.tableHeaderNumber}>{t('charges:per-month')}</TableCell>
            <TableCell className={classes.tableHeaderNumber}>
              {t('charges:number-of-month')}
            </TableCell>
          </>
        )}

        {!hideTax && <TableCell className={classes.tableHeaderNumber}>{t('charges:tax', { defaultValue: 'Tax' })}</TableCell>}

        <TableCell
          className={classes.tableHeaderNumber}
          style={isOrderTypeNotHUD1 ? { borderLeft: hairline } : {}}
        >
          {isOrderTypeNotHUD1 ? t('charges:at-closing') : t('charges:paidByBorrower')}
        </TableCell>

        {isOrderTypeNotHUD1 && (
          <TableCell
            className={classes.tableHeaderNumber}
            style={{ borderRight: hairline, paddingRight: '5px' }}
          >
            {t('charges:before-closing')}
          </TableCell>
        )}

        <TableCell className={classes.tableHeaderNumber}>
          {isOrderTypeNotHUD1 ? t('charges:at-closing') : t('charges:paidBySeller')}
        </TableCell>

        {isOrderTypeNotHUD1 && (
          <TableCell
            className={classes.tableHeaderNumber}
            style={{ borderRight: hairline, paddingRight: '5px' }}
          >
            {t('charges:before-closing')}
          </TableCell>
        )}

        {isOrderTypeNotHUD1 && (
          <TableCell className={classes.tableHeaderByOthers}>
            <>{t('charges:by-others')}</>
          </TableCell>
        )}

        {isOrderTypeNotHUD1 && (
          <TableCell className={classes.tableHeaderAction}>
            <FontAwesomeIcon
              icon="users"
              className={classes.payerIcon}
              style={{ cursor: 'pointer' }}
              onClick={() => onEditSplitPayers({} as ChargeFormEntry, "Payer", true)}
            />
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
};
